/* ==== Course page is cached on cdn for 1 hours ==== */
import { SWRConfig } from 'swr';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Course from '@cont/Course/Course';
import { getIsFetchingUserData } from '@selectors/auth';
import { fetchCourseData } from '@api/fetchers/freelesson';
import {
  fetchLessonList,
  fetchSimilarPlusCourses,
  fetchRelevantFreeCourses
} from '@api/fetchers/course';
import { UnAuthorizedError } from '@utils/APICustomErrors';
import SEO from '@seo/index';
import courseSchema from '@seo/course/course';
import { wordsToSentence } from '@utils/textHelper';
import cookies, { readCookie } from '@utils/cookies';
import { COURSE_PAGE } from '@constants/segment';
import { emptyObject } from '@constants/empty';
import {
  processCourseData,
  processLessonList,
  processSimilarPlusCoursesData,
  processRelevantFreeCoursesData
} from '@api/hooks/course/utils';
import useCourseInfo, {
  getKey as getCourseInfoKey
} from '@api/hooks/course/useCourseInfo';
import { getKey as getLessonListKey } from '@api/hooks/course/useLessonList';
import { getKey as getSimilarPlusCoursesKey } from '@api/hooks/course/useSimilarPlusCourses';
import { getKey as getRelevantCoursesKey } from '@api/hooks/course/useRelevantFreeCourses';
import Layout from '@layout/index';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useToggleable } from '@stores/toggleables';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import { fetchCourseDemoClasses } from '@api/fetchers/batch';
import { getCourseDemoClassesKey } from '@api/hooks/batch/useDemoClasses';
import { fetchFreeTrialInfo } from '@cont/NewPreSubscribed/FreeTrial/api';
import validateAndSetSessionId, {
  DEVICE_ID_COOKIE_NAME
} from '@utils/session/validateAndSetSessionId';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { abProxyJWT } from '@utils/abHelper';
import useFollowGoal from '@api/hooks/useFollowGoal';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';

const CoursePageWithSEO = (props) => {
  const {
    query: { courseUID }
  } = useRouter();
  const { freeTrialData } = props || {};
  const { course } = useCourseInfo(courseUID);

  const fetchingUserData = useSelector(getIsFetchingUserData);

  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { followCurrentGoal } = useFollowGoal(course?.goal?.uid);
  useEffect(() => {
    if (isLoggedIn) {
      followCurrentGoal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  useEffect(() => {
    if (!course.uid) {
      if (!readCookie('accessToken') && !fetchingUserData) {
        openLoginDrawer();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    name = '',
    description,
    slug,
    uid,
    isPublic,
    thumbnail,
    coverPhoto,
    ogImage,
    authors,
    language,
    goal = emptyObject
  } = course;

  useEffect(() => {
    validateAndSetSessionId();
    const deviceID = cookies.readCookie(DEVICE_ID_COOKIE_NAME);
    fetchABExperimentData(
      'seven_day_free_trial_web_experiment',
      abProxyJWT,
      deviceID
    );
  }, []);

  const metaTitle = `${goal.name || 'Course'} - ${name} by Unacademy`;
  const metaDescription = `Enrol for ${
    goal.name || 'Course'
  } ${name} conducted by ${wordsToSentence(
    authors,
    'name',
    ',',
    ','
  )} on Unacademy. The course is taught in ${language}.`;
  const url =
    slug && uid ? `https://unacademy.com/course/${slug}/${uid}` : null;

  const schema =
    uid && isPublic ? courseSchema(goal, description, course) : null;

  return (
    <>
      <SEO
        title={metaTitle}
        url={url}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          metaDescription,
          images: [
            {
              url: ogImage || thumbnail || coverPhoto
            }
          ],
          url
        }}
        schema={schema}
      />
      <Course courseUID={courseUID} freeTrialData={freeTrialData} />
      <OneLinkSmartScript />
    </>
  );
};

const CourseWithSWRConfig = ({
  courseUID,
  courseFallbackData,
  freeTrialData,
  lessonListFallbackData,
  similarPlusCoursesFallbackData,
  relevantFreeCoursesFallbackData,
  demoClasses
}) => {
  const courseInfokey = getCourseInfoKey(courseUID);
  const lessonListKey = getLessonListKey(courseUID);
  const similarPlusCoursesKey = getSimilarPlusCoursesKey(courseUID);
  const relevantFreeCoursesKey = getRelevantCoursesKey(courseUID);
  const demoClassesKey = getCourseDemoClassesKey(courseUID);
  const accessToken = readCookie('accessToken');
  return (
    <SWRConfig
      value={{
        revalidateOnMount: !!accessToken,
        dedupingInterval: 60 * 1000,
        fallback: {
          [courseInfokey]: courseFallbackData,
          [similarPlusCoursesKey]: similarPlusCoursesFallbackData,
          [lessonListKey]: processLessonList(lessonListFallbackData),
          [relevantFreeCoursesKey]: relevantFreeCoursesFallbackData,
          [demoClassesKey]: demoClasses
        }
      }}
    >
      <CoursePageWithSEO courseUID={courseUID} freeTrialData={freeTrialData} />
    </SWRConfig>
  );
};

CourseWithSWRConfig.pageInfo = {
  name: COURSE_PAGE,
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true
  },
  isMWebSupported: true
};

export const getStaticProps = async ({ params }) => {
  try {
    const course = await fetchCourseData(params.courseUID);

    if (!course?.uid) {
      return { notFound: true };
    }

    const promises = [fetchLessonList(params.courseUID)];

    const processedCourseData = processCourseData(course);
    const { isSpecial, slug, batchGroup } = processedCourseData;

    if (isSpecial) {
      const [lessonList] = await Promise.all(promises);
      const specialClassUID = lessonList?.results?.[0]?.value?.uid;

      if (specialClassUID) {
        return {
          redirect: {
            permanent: true,
            destination: `/class/${slug}/${specialClassUID}/`
          }
        };
      }
    }

    if (batchGroup?.relativeLink) {
      return {
        redirect: {
          permanent: true,
          destination: batchGroup.relativeLink
        }
      };
    }

    const fallbacks = {};

    if (processedCourseData.forPlus) {
      promises.push(fetchSimilarPlusCourses(params.courseUID));

      const [lessonList, similarPlusCourses] = await Promise.all(promises);

      fallbacks.lessonListFallbackData = lessonList;
      fallbacks.similarPlusCoursesFallbackData = processSimilarPlusCoursesData(
        params.courseUID,
        similarPlusCourses
      );
    } else {
      promises.push(fetchRelevantFreeCourses(params.courseUID));

      const [lessonList, relevantFreeCourses] = await Promise.all(promises);

      fallbacks.lessonListFallbackData = lessonList;
      fallbacks.relevantFreeCoursesFallbackData =
        processRelevantFreeCoursesData(relevantFreeCourses);
    }

    const demoClasses = await fetchCourseDemoClasses(params.courseUID);
    const freeTrialData = await fetchFreeTrialInfo(
      processedCourseData?.goalUid,
      null,
      params?.courseUID
    );
    return {
      props: {
        key: params.courseUID,
        courseUID: params.courseUID,
        courseFallbackData: processedCourseData,
        freeTrialData,
        demoClasses,
        ...fallbacks
      },
      revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
    };
  } catch (err) {
    if (err instanceof UnAuthorizedError) {
      return {
        props: {
          key: params.courseUID,
          courseUID: params.courseUID,
          courseFallbackData: {
            processed: true
          },
          lessonListFallbackData: {
            results: [],
            processed: true
          },
          relevantFreeCoursesFallbackData: []
        },
        revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
      };
    }

    return { notFound: true, revalidate: 5 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default CourseWithSWRConfig;
CourseWithSWRConfig.Layout = Layout;
CourseWithSWRConfig.Wrapper = PageReduxProvider;
